/**
 * Storezone — Theme Context
 *
 * Controls the active theme applied to the <html> element.
 *
 * Three built-in themes:
 *   'storezone'  — platform default (blue)
 *   'superadmin' — platform admin view (violet)
 *   'tenant'     — runtime-configured from tenant_branding
 *
 * The ThemeProvider applies data-theme="X" to document.documentElement.
 * For the 'tenant' theme, it also calls applyTenantBrandingToDOM()
 * to inject dynamic CSS custom properties derived from primary_color.
 *
 * Usage:
 *   Wrap app with <ThemeProvider>
 *   Use useTheme() to read/change the active theme
 *   Use applyTenantBranding() to load tenant brand colors
 */

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import {
  applyTenantBrandingToDOM,
  removeTenantBrandingFromDOM,
  type TenantBranding,
} from '@/lib/branding'

// ============================================================
// Types
// ============================================================

export type ThemeName = 'storezone' | 'superadmin' | 'tenant'

export interface ThemeContextValue {
  /** Currently active theme */
  theme: ThemeName

  /** Switch to a built-in theme */
  setTheme: (theme: ThemeName) => void

  /** Apply tenant branding and switch to 'tenant' theme */
  applyTenantBranding: (branding: TenantBranding) => void

  /** Clear tenant branding and revert to 'storezone' theme */
  clearTenantBranding: () => void

  /** Active branding data (when theme === 'tenant') */
  tenantBranding: TenantBranding | null
}

// ============================================================
// Context
// ============================================================

export const ThemeContext = createContext<ThemeContextValue | null>(null)

// ============================================================
// Provider
// ============================================================

interface ThemeProviderProps {
  children: ReactNode
  /** Initial theme — useful for zone-specific providers */
  initialTheme?: ThemeName
}

export function ThemeProvider({
  children,
  initialTheme = 'storezone',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(initialTheme)
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null)

  // Apply theme attribute to <html> whenever theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    return () => {
      // Do not remove on unmount — keeps the theme during re-renders
    }
  }, [theme])

  const setTheme = useCallback((next: ThemeName) => {
    if (next !== 'tenant') {
      removeTenantBrandingFromDOM()
      setTenantBranding(null)
    }
    setThemeState(next)
  }, [])

  const applyTenantBranding = useCallback((branding: TenantBranding) => {
    applyTenantBrandingToDOM(branding)
    setTenantBranding(branding)
    setThemeState('tenant')
  }, [])

  const clearTenantBranding = useCallback(() => {
    removeTenantBrandingFromDOM()
    setTenantBranding(null)
    setThemeState('storezone')
  }, [])

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        applyTenantBranding,
        clearTenantBranding,
        tenantBranding,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
